import { useState } from 'react'
import Ladehinweis from '../../komponenten/Ladehinweis'
import { useLaden } from '../../lib/laden'
import {
  holeKanaele,
  type Kanal,
  type KanalAktiv,
  type KanalGesamt,
  type KanalWoche,
  type Zeitraum,
} from '../../lib/kennzahlen'

/**
 * Über welchen Kanal neue Nutzer kommen — und wie gut die Kanäle sind.
 *
 * **Die Seite rechnet nichts nach.** Alle Zahlen stammen fertig aus zwei
 * Views (Migration 073 im Cloud-Repo): was „echt", „mit Beitrag" und
 * „Wechsler" heißt, steht dort. Hier wird nur zusammengezählt, was ohnehin
 * nebeneinander steht — die Summenzeile und die Höhe einer Säule.
 */

/** Unten nach oben im Stapel — die 1.0 als Sockel, das Neue obenauf. */
const REIHENFOLGE: Kanal[] = ['app-1.0', 'ios', 'web', 'unbekannt']

const NAME: Record<Kanal, string> = {
  'app-1.0': 'App 1.0',
  ios: 'App 2.x · iOS',
  web: 'Web',
  unbekannt: 'unbekannt',
}

const ZEITRAEUME: { wert: Zeitraum; text: string }[] = [
  { wert: 12, text: '12 Wochen' },
  { wert: 26, text: '26' },
  { wert: 52, text: '52' },
  { wert: null, text: 'alles' },
]

const TAG = 86_400_000

/**
 * Alle Montage von `von` bis `bis`, beide eingeschlossen.
 *
 * In UTC gerechnet: Mit Ortszeit würde über die Zeitumstellung aus einem
 * Montag ein Sonntag, und eine Säule stünde neben ihrer Woche.
 */
function montage(von: string, bis: string): string[] {
  const ergebnis: string[] = []
  const ende = Date.parse(`${bis}T00:00:00Z`)
  for (let t = Date.parse(`${von}T00:00:00Z`); t <= ende; t += 7 * TAG) {
    ergebnis.push(new Date(t).toISOString().slice(0, 10))
  }
  return ergebnis
}

function minusWochen(montag: string, n: number): string {
  return new Date(Date.parse(`${montag}T00:00:00Z`) - n * 7 * TAG)
    .toISOString()
    .slice(0, 10)
}

const zahl = (n: number) => n.toLocaleString('de-DE')

/** Anteil in ganzen Prozent; ohne Nenner ein Strich statt „0 %". */
function anteil(teil: number, ganzes: number): string {
  return ganzes === 0 ? '—' : `${Math.round((teil / ganzes) * 100)} %`
}

function wocheText(montag: string, lang: boolean): string {
  const d = new Date(`${montag}T00:00:00Z`)
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    ...(lang ? { year: '2-digit' } : {}),
    timeZone: 'UTC',
  })
}

export default function Kanaele() {
  const [zeitraum, setZeitraum] = useState<Zeitraum>(12)
  const [zustand] = useLaden(() => holeKanaele(zeitraum), [zeitraum])

  return (
    <section className="stapel">
      <div className="stapel-kopf">
        <h1>Kanäle</h1>
        {zustand.art === 'da' && (
          <p className="gedaempft zahl">
            {zahl(zustand.wert.wochen.reduce((s, w) => s + w.neue_konten, 0))} neue
            Konten ·{' '}
            {zeitraum === null ? 'seit Beginn' : `letzte ${zeitraum} Wochen`}
          </p>
        )}
      </div>

      {/* Der Wähler steht außerhalb des Ladezustands: Er soll beim
          Umschalten nicht verschwinden und wieder auftauchen. */}
      <div className="segmente" role="radiogroup" aria-label="Zeitraum">
        {ZEITRAEUME.map((z) => (
          <label key={z.text} className={z.wert === zeitraum ? 'segment aktiv' : 'segment'}>
            <input
              type="radio"
              name="zeitraum"
              checked={z.wert === zeitraum}
              onChange={() => setZeitraum(z.wert)}
            />
            <span>{z.text}</span>
          </label>
        ))}
      </div>

      {zustand.art !== 'da' ? (
        <Ladehinweis zustand={zustand} />
      ) : (
        <Inhalt
          wochen={zustand.wert.wochen}
          aktiv={zustand.wert.aktiv}
          gesamt={zustand.wert.gesamt}
          dieseWoche={zustand.wert.dieseWoche}
          zeitraum={zeitraum}
        />
      )}
    </section>
  )
}

function Inhalt({
  wochen,
  aktiv,
  gesamt,
  dieseWoche,
  zeitraum,
}: {
  wochen: KanalWoche[]
  aktiv: KanalAktiv[]
  gesamt: KanalGesamt[]
  dieseWoche: string
  zeitraum: Zeitraum
}) {
  // „Wochen ohne Konto fehlen in der View — das Diagramm zeigt sie als leere
  // Spalte, nicht als Lücke." Also das Raster selbst aufspannen und die View
  // hineinlegen, statt nur die Wochen zu zeichnen, die zufällig da sind.
  const aelteste = wochen.reduce((m, w) => (w.woche < m ? w.woche : m), dieseWoche)
  const von = zeitraum === null ? aelteste : minusWochen(dieseWoche, zeitraum - 1)
  const raster = montage(von, dieseWoche)

  const jeWoche = new Map<string, Partial<Record<Kanal, KanalWoche>>>()
  for (const w of wochen) {
    const eintrag = jeWoche.get(w.woche) ?? {}
    eintrag[w.kanal] = w
    jeWoche.set(w.woche, eintrag)
  }

  const kanaeleDa = REIHENFOLGE.filter((k) => wochen.some((w) => w.kanal === k))
  const unbekannt = wochen.filter((w) => w.kanal === 'unbekannt')

  return (
    <>
      <Kacheln aktiv={aktiv} gesamt={gesamt} />

      {unbekannt.length > 0 && (
        <p className="meldung meldung-warnung">
          <strong>
            {zahl(unbekannt.reduce((s, w) => s + w.neue_konten, 0))} Konten ohne
            Kanal
          </strong>{' '}
          in diesem Zeitraum. Laut Migration 073 sollte es die nicht geben — das
          gehört an den Schema-Chat, nicht in die Moderation.
        </p>
      )}

      <div className="karte">
        <h3>Neue Konten je Woche</h3>
        <Legende kanaele={kanaeleDa} />
        <Saeulen raster={raster} jeWoche={jeWoche} lang={raster.length > 52} />
        {zeitraum === null && (
          <p className="leise gesehen-zeile">
            Die Konten der App 1.0 stehen in ihren echten Entstehungswochen —
            das Datum kommt aus der alten App. Deshalb reicht der graue Sockel
            Jahre zurück, obwohl der Kanal erst seit dem 15.09.2026 so heißt.
          </p>
        )}
      </div>

      <div className="karte">
        <h3>Je Woche und Kanal</h3>
        <Tabelle wochen={wochen} kanaele={kanaeleDa} ganzeZeit={zeitraum === null} />
      </div>
    </>
  )
}

function Kacheln({ aktiv, gesamt }: { aktiv: KanalAktiv[]; gesamt: KanalGesamt[] }) {
  // Nur die beiden Kanäle mit Geräten. Fehlt einer in der View, hat er noch
  // kein einziges Gerät — dann ist 0 die richtige Zahl, nicht „keine Angabe".
  const zeile = (k: 'ios' | 'web') => aktiv.find((a) => a.kanal === k)
  const alt = gesamt.find((g) => g.kanal === 'app-1.0')

  return (
    <>
      <div className="zahlen-raster">
        {(['ios', 'web'] as const).map((k) => {
          const a = zeile(k)
          return (
            <div key={k} className="karte zahl-karte">
              <span className="gedaempft">
                <span className={`kanal-punkt kanal-${k}`} aria-hidden="true" />
                {NAME[k]}
              </span>
              <span className="zahl-gross zahl">{zahl(a?.aktiv_7_tage ?? 0)}</span>
              <span className="leise">
                aktiv in 7 Tagen · 30 Tage:{' '}
                <span className="zahl">{zahl(a?.aktiv_30_tage ?? 0)}</span>
              </span>
            </div>
          )
        })}

        {/* Die Zahl, nach der zuerst gefragt wird: Wie viele aus der 1.0 sind
            herübergekommen? Sie steht über die ganze Zeit hier, nicht über den
            gewählten Zeitraum — die Wechsler verteilen sich über Jahre, und in
            den letzten 12 Wochen stünde nur ein Drittel von ihnen. */}
        {alt && (
          <div className="karte zahl-karte">
            <span className="gedaempft">
              <span className="kanal-punkt kanal-app-1.0" aria-hidden="true" />
              Aus der App 1.0 gewechselt
            </span>
            <span className="zahl-gross zahl">{zahl(alt.davon_wechsler)}</span>
            <span className="leise">
              von <span className="zahl">{zahl(alt.neue_konten)}</span> Konten der
              1.0 · seit Beginn
            </span>
          </div>
        )}
      </div>
      <p className="leise gesehen-zeile">
        Aktiv: Konten mit einem Gerät dieses Kanals. Wer iOS und Web nutzt,
        zählt in beiden Kacheln; die App 1.0 meldet keine Geräte und hat
        deshalb keine Aktivzahl. Gewechselt: Konten aus der 1.0, die heute ein
        iOS- oder Web-Gerät haben — über die ganze Zeit gezählt, nicht nur im
        gewählten Zeitraum.
      </p>
    </>
  )
}

function Legende({ kanaele }: { kanaele: Kanal[] }) {
  return (
    <ul className="legende">
      {kanaele.map((k) => (
        <li key={k}>
          <span className={`kanal-punkt kanal-${k}`} aria-hidden="true" />
          {NAME[k]}
        </li>
      ))}
    </ul>
  )
}

/**
 * Welche Wochen gekappt werden: **einzelne Spitzen**, nicht hohe Wochen.
 *
 * Nötig wegen zweier Wochen aus dem Import der 1.0 (gemessen am 22.09.2026:
 * 10.08.2026 mit 720, 27.07.2026 mit 169 Konten). Ohne Kappung wäre die
 * Skala 720 hoch, und die ersten iOS-Wochen mit 12 bis 20 Konten stünden als
 * Striche von zwei Prozent da — genau die Frage, für die es die Seite gibt,
 * wäre in der Standardansicht unsichtbar.
 *
 * Gekappt wird eine Woche nur, wenn **beides** gilt:
 *
 *   · mehr als das Vierfache des Medians aller Wochen mit Konten — sie fällt
 *     aus dem Üblichen;
 *   · mehr als das Dreifache **beider** Nachbarwochen — sie steht allein.
 *
 * Die zweite Bedingung ist die wichtige. Mit dem Median allein würde echtes
 * Wachstum gekappt: Sobald die neue App anzieht, lägen ihre Wochen über dem
 * Vierfachen einer Grundlinie aus der Zeit davor, und die Seite versteckte
 * genau den Anstieg, den sie zeigen soll. Wachstum ist stetig — die
 * Nachbarwochen sind ähnlich hoch. Ein Import ist eine Spitze: 720 zwischen
 * 31 und 31.
 *
 * Mit weniger als vier Wochen gibt es keinen Median, dem man trauen könnte —
 * dann wird nichts gekappt.
 */
function spitzen(summen: number[]): boolean[] {
  const n = summen.filter((x) => x > 0).sort((a, b) => a - b)
  if (n.length < 4) return summen.map(() => false)
  const mitte = Math.floor(n.length / 2)
  const median = n.length % 2 ? (n[mitte] ?? 0) : ((n[mitte - 1] ?? 0) + (n[mitte] ?? 0)) / 2

  return summen.map((wert, i) => {
    // Am Rand zählt der eine Nachbar, den es gibt.
    const nachbar = Math.max(summen[i - 1] ?? 0, summen[i + 1] ?? 0)
    return wert > 4 * median && wert > 3 * nachbar
  })
}

/**
 * Gestapelte Säulen in reinem CSS — es sind höchstens einige hundert
 * Rechtecke, dafür braucht es keine Diagrammbibliothek.
 *
 * Die Höhe einer Säule ist ihr Anteil an der höchsten Woche im Zeitraum; die
 * Segmente teilen diese Höhe nach `flex-grow` unter sich auf. Die genauen
 * Zahlen stehen im Tooltip und, für Screenreader, vollständig in der Tabelle
 * darunter.
 */
function Saeulen({
  raster,
  jeWoche,
  lang,
}: {
  raster: string[]
  jeWoche: Map<string, Partial<Record<Kanal, KanalWoche>>>
  lang: boolean
}) {
  const summe = (m: string) =>
    REIHENFOLGE.reduce((s, k) => s + (jeWoche.get(m)?.[k]?.neue_konten ?? 0), 0)
  const summen = raster.map(summe)
  const gekapptJe = spitzen(summen)
  const skala = Math.max(1, ...summen.filter((_, i) => !gekapptJe[i]))
  const gekappt = gekapptJe.filter(Boolean).length
  // Etwa acht Beschriftungen, egal wie viele Säulen.
  const schritt = Math.max(1, Math.ceil(raster.length / 8))

  return (
    <div className="saeulen-rahmen">
      <p className="leise zahl saeulen-skala">
        Skala bis {zahl(skala)}
        {gekappt > 0 &&
          ` · ${gekappt} ${gekappt === 1 ? 'Woche' : 'Wochen'} gekappt, die Zahl steht darüber`}
      </p>
      <div
        // Ab etwa 80 Säulen wäre der Zwischenraum breiter als die Säule selbst.
        className={raster.length > 80 ? 'saeulen dicht' : 'saeulen'}
        role="img"
        aria-label={`Neue Konten je Woche, ${raster.length} Wochen. Die Zahlen stehen in der Tabelle darunter.`}
      >
        {raster.map((m, i) => {
          const w = jeWoche.get(m) ?? {}
          const gesamt = summe(m)
          const titel =
            `Woche ab ${wocheText(m, true)}: ${zahl(gesamt)} neue Konten` +
            REIHENFOLGE.filter((k) => w[k])
              .map((k) => `\n${NAME[k]}: ${zahl(w[k]?.neue_konten ?? 0)}`)
              .join('')
          const zuHoch = gekapptJe[i] ?? false
          return (
            <div key={m} className={zuHoch ? 'saeule gekappt' : 'saeule'} title={titel}>
              {zuHoch && <span className="saeule-marke zahl">{zahl(gesamt)}</span>}
              <div
                className="saeule-stapel"
                style={{ height: `${Math.min(1, gesamt / skala) * 100}%` }}
              >
                {REIHENFOLGE.map((k) =>
                  w[k]?.neue_konten ? (
                    <div
                      key={k}
                      className={`saeule-teil kanal-${k}`}
                      style={{ flexGrow: w[k]?.neue_konten }}
                    />
                  ) : null,
                )}
              </div>
            </div>
          )
        })}
      </div>
      <div
        className={raster.length > 80 ? 'saeulen-achse dicht' : 'saeulen-achse'}
        aria-hidden="true"
      >
        {raster.map((m, i) => (
          <span key={m}>{i % schritt === 0 ? wocheText(m, lang) : ''}</span>
        ))}
      </div>
    </div>
  )
}

function Tabelle({
  wochen,
  kanaele,
  ganzeZeit,
}: {
  wochen: KanalWoche[]
  kanaele: Kanal[]
  ganzeZeit: boolean
}) {
  // „Summe" allein ließe offen, worüber — und die Kachel darüber zählt die
  // Wechsler über die ganze Zeit. Zwei verschiedene Zahlen unter demselben
  // Wort hielte man für einen Fehler.
  const summeText = ganzeZeit ? 'Summe' : 'Summe Zeitraum'
  const summe = (k: Kanal | null) => {
    const zeilen = k === null ? wochen : wochen.filter((w) => w.kanal === k)
    return {
      neue: zeilen.reduce((s, w) => s + w.neue_konten, 0),
      echt: zeilen.reduce((s, w) => s + w.davon_echt, 0),
      beitrag: zeilen.reduce((s, w) => s + w.davon_mit_beitrag, 0),
      wechsler: zeilen.reduce((s, w) => s + w.davon_wechsler, 0),
    }
  }

  if (wochen.length === 0) {
    return <p className="gedaempft">In diesem Zeitraum ist kein Konto entstanden.</p>
  }

  return (
    <div className="tabelle-huelle">
      <table className="tabelle kanal-tabelle">
        <thead>
          <tr>
            <th>Woche ab</th>
            <th>Kanal</th>
            <th className="rechts">neu</th>
            <th className="rechts">echt</th>
            <th className="rechts">mit Beitrag</th>
            <th className="rechts">Wechsler</th>
            <th className="rechts">Anteil echt</th>
          </tr>
        </thead>
        <tbody>
          {/* Die View liefert schon neueste Woche zuerst — keine eigene
              Sortierung, wie verlangt. */}
          {wochen.map((w) => (
            <tr key={`${w.woche}-${w.kanal}`}>
              <td className="zahl">{wocheText(w.woche, true)}</td>
              <td>
                <span className={`kanal-punkt kanal-${w.kanal}`} aria-hidden="true" />
                {NAME[w.kanal]}
              </td>
              <td className="zahl rechts">{zahl(w.neue_konten)}</td>
              <td className="zahl rechts">{zahl(w.davon_echt)}</td>
              <td className="zahl rechts">{zahl(w.davon_mit_beitrag)}</td>
              <td className="zahl rechts">{zahl(w.davon_wechsler)}</td>
              <td className="zahl rechts">{anteil(w.davon_echt, w.neue_konten)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          {kanaele.map((k) => {
            const s = summe(k)
            return (
              <tr key={k}>
                <td>{summeText}</td>
                <td>
                  <span className={`kanal-punkt kanal-${k}`} aria-hidden="true" />
                  {NAME[k]}
                </td>
                <td className="zahl rechts">{zahl(s.neue)}</td>
                <td className="zahl rechts">{zahl(s.echt)}</td>
                <td className="zahl rechts">{zahl(s.beitrag)}</td>
                <td className="zahl rechts">{zahl(s.wechsler)}</td>
                <td className="zahl rechts">{anteil(s.echt, s.neue)}</td>
              </tr>
            )
          })}
          {kanaele.length > 1 &&
            (() => {
              const s = summe(null)
              return (
                <tr className="summe-gesamt">
                  <td>{summeText}</td>
                  <td>alle Kanäle</td>
                  <td className="zahl rechts">{zahl(s.neue)}</td>
                  <td className="zahl rechts">{zahl(s.echt)}</td>
                  <td className="zahl rechts">{zahl(s.beitrag)}</td>
                  <td className="zahl rechts">{zahl(s.wechsler)}</td>
                  <td className="zahl rechts">{anteil(s.echt, s.neue)}</td>
                </tr>
              )
            })()}
        </tfoot>
      </table>
    </div>
  )
}
